import { bridge } from '../bridge'

// Thin wrappers over the keychain bridge with the correct sync semantics
// for each key. io_device credentials are LOCAL (never iCloud-synced — each
// physical device needs a distinct io_device_id); humanUserID and
// organizationID are iCloud-synced so a user signing in on a new phone
// gets back into the same account.

export const keychain = {
  async ioDeviceID(): Promise<string | null> {
    const { value } = await bridge.call('keychain.get', {
      key: 'ioDeviceID',
      sync: false,
    })
    return value
  },
  async setIoDeviceID(value: string) {
    await bridge.call('keychain.set', { key: 'ioDeviceID', value, sync: false })
  },
  async ioDeviceSecret(): Promise<string | null> {
    const { value } = await bridge.call('keychain.get', {
      key: 'ioDeviceSecret',
      sync: false,
    })
    return value
  },
  async setIoDeviceSecret(value: string) {
    await bridge.call('keychain.set', {
      key: 'ioDeviceSecret',
      value,
      sync: false,
    })
  },
  async humanUserID(): Promise<string | null> {
    const { value } = await bridge.call('keychain.get', {
      key: 'humanUserID',
      sync: true,
    })
    return value
  },
  async setHumanUserID(value: string) {
    await bridge.call('keychain.set', {
      key: 'humanUserID',
      value,
      sync: true,
    })
  },
  async organizationID(): Promise<string | null> {
    const { value } = await bridge.call('keychain.get', {
      key: 'organizationID',
      sync: true,
    })
    return value
  },
  async setOrganizationID(value: string) {
    await bridge.call('keychain.set', {
      key: 'organizationID',
      value,
      sync: true,
    })
  },
  async email(): Promise<string | null> {
    const { value } = await bridge.call('keychain.get', {
      key: 'email',
      sync: true,
    })
    return value
  },
  async setEmail(value: string) {
    await bridge.call('keychain.set', { key: 'email', value, sync: true })
  },
  async wipeDeviceCreds() {
    await bridge.call('keychain.delete', { key: 'ioDeviceID', sync: false })
    await bridge.call('keychain.delete', { key: 'ioDeviceSecret', sync: false })
  },
  /// Wipe everything — both the local io_device creds and the
  /// iCloud-synced account identity. Used by Logout. Synced deletes are
  /// intentional: if the user picks a different account after logout,
  /// we don't want a stale humanUserID on another device to get merged
  /// into the new identity on iCloud sync.
  async wipeAll() {
    await Promise.all([
      bridge.call('keychain.delete', { key: 'ioDeviceID', sync: false }),
      bridge.call('keychain.delete', { key: 'ioDeviceSecret', sync: false }),
      bridge.call('keychain.delete', { key: 'humanUserID', sync: true }),
      bridge.call('keychain.delete', { key: 'organizationID', sync: true }),
      bridge.call('keychain.delete', { key: 'email', sync: true }),
    ])
  },
}
